// Tizen 2.4's WebKit 538 (Samsung 2016) defaults `writable: false` for
// `Object.defineProperty` data descriptors that don't specify writable, and
// throws "Attempting to change value of a readonly property" when those
// properties are subsequently reassigned. Modern V8 is silently permissive
// here, so most bundled libs (webpack runtime helpers, Apollo, shapeshifter,
// zod, react-dom internals) don't set `writable: true` explicitly and break.
//
// Patch defineProperty to default `writable: true` for data descriptors when
// not explicitly set — matches V8's effective behavior. Must run before any
// other import so the polyfills below also benefit. Wrapped in try/catch so a
// failure to monkey-patch never blocks boot.
try {
  const origDP = Object.defineProperty;
  Object.defineProperty = function <T>(
    target: T,
    key: PropertyKey,
    desc: PropertyDescriptor
  ): T {
    try {
      if (
        desc &&
        !('get' in desc) &&
        !('set' in desc) &&
        !('writable' in desc)
      ) {
        desc.writable = true;
      }
      return origDP(target as object, key, desc) as T;
    } catch {
      try {
        if (desc && 'value' in desc) {
          (target as Record<PropertyKey, unknown>)[key] = desc.value;
        }
      } catch {
        // even direct assignment failed — caller will see the missing prop
      }
      return target;
    }
  };
} catch {
  // engine refuses to monkey-patch — leave native impl alone
}

((window as unknown) as {
  process: { env: Record<string, string> };
}).process = ((window as unknown) as {
  process?: { env: Record<string, string> };
}).process || { env: {} };

import 'react-app-polyfill/ie9';
import 'react-app-polyfill/stable';
import 'allsettled-polyfill';
import 'intersection-observer';
import 'scrollyfills';
import 'focus-within-polyfill';
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import 'proxy-polyfill';
import globalThisPolyfill from 'globalthis';
import { enableES5 as enableES5ForImmer } from 'immer';
import JSBI from 'jsbi';

if (typeof BigInt === 'undefined') {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  window.BigInt = JSBI.BigInt;
}

globalThisPolyfill.shim();

if (!Object.hasOwn) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  Object.hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
}

// Tizen 2.4's WebKit-based engine throws "readonly property" on direct
// assignment to certain pre-defined globals (even when typeof returns
// 'undefined' — the slot is reserved). Use defineProperty with explicit
// writable/configurable, fall back to direct assignment, and swallow
// failures so a single rejected polyfill doesn't kill the whole boot.
function safeDefine(target: unknown, key: string, value: unknown) {
  try {
    Object.defineProperty(target as object, key, {
      value,
      writable: true,
      configurable: true,
      enumerable: false
    });
  } catch {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (target as any)[key] = value;
    } catch {
      // give up — host engine refuses both paths
    }
  }
}

// Apollo Client 4 uses FinalizationRegistry + WeakRef for memory hints (Chrome 84+).
// Tizen 2/3/4/5 doesn't ship them — stub as no-ops so Apollo loads.
if (typeof (window as unknown as { FinalizationRegistry?: unknown }).FinalizationRegistry === 'undefined') {
  safeDefine(
    window,
    'FinalizationRegistry',
    class {
      register() {}
      unregister() {}
    }
  );
}
if (typeof (window as unknown as { WeakRef?: unknown }).WeakRef === 'undefined') {
  safeDefine(
    window,
    'WeakRef',
    class<T> {
      private t: T;
      constructor(target: T) {
        this.t = target;
      }
      deref(): T {
        return this.t;
      }
    }
  );
}

// DOMException constructor (Chrome 46+) — pre-2015 WebKit exposes
// `DOMException` as a non-constructible interface, so `new DOMException()`
// throws "Illegal constructor". Apollo 4's AbortController error path in
// ObservableQuery#finalize calls `new DOMException(msg, 'AbortError')` on
// every useQuery teardown — crash bubbles up as UnsubscriptionError. Repros
// on LG webOS 3 (Chrome 38) and Tizen 2.4–3. typeof returns "function" so a
// presence check passes; we probe constructability instead.
try {
  // eslint-disable-next-line no-new
  new ((window as unknown) as {
    DOMException: new (m: string, n: string) => unknown;
  }).DOMException('probe', 'AbortError');
} catch {
  safeDefine(
    window,
    'DOMException',
    class DOMExceptionShim extends Error {
      constructor(message?: string, name?: string) {
        super(message);
        this.name = name || 'Error';
      }
    }
  );
}

// queueMicrotask (Chrome 71+) — used by React 18 + Apollo internals.
if (
  typeof (window as unknown as { queueMicrotask?: unknown }).queueMicrotask !==
  'function'
) {
  safeDefine(window, 'queueMicrotask', (cb: () => void) => {
    Promise.resolve()
      .then(cb)
      .catch((e) =>
        setTimeout(() => {
          throw e;
        }, 0)
      );
  });
}

// Object.fromEntries (Chrome 73+).
if (typeof Object.fromEntries !== 'function') {
  safeDefine(Object, 'fromEntries', (entries: Iterable<[PropertyKey, unknown]>) => {
    const out: Record<string, unknown> = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const it: any = (entries as any)[Symbol.iterator]
      ? (entries as any)[Symbol.iterator]()
      : entries;
    let step = it.next();
    while (!step.done) {
      const [k, v] = step.value;
      out[String(k)] = v;
      step = it.next();
    }
    return out;
  });
}

// Array.prototype.flat (Chrome 69+).
if (typeof Array.prototype.flat !== 'function') {
  safeDefine(Array.prototype, 'flat', function (this: unknown[], depth?: number) {
    const d = depth === undefined ? 1 : Number(depth);
    const out: unknown[] = [];
    const walk = (arr: unknown[], cur: number) => {
      for (let i = 0; i < arr.length; i++) {
        const v = arr[i];
        if (Array.isArray(v) && cur > 0) {
          walk(v as unknown[], cur - 1);
        } else {
          out.push(v);
        }
      }
    };
    walk(this as unknown[], d);
    return out;
  });
}

// Array.prototype.flatMap (Chrome 69+).
if (typeof Array.prototype.flatMap !== 'function') {
  safeDefine(Array.prototype, 'flatMap', function (
    this: unknown[],
    fn: (this: unknown, x: unknown, i: number, arr: unknown[]) => unknown,
    thisArg?: unknown
  ) {
    return (this as unknown[]).map(fn, thisArg).flat();
  });
}

// structuredClone (Chrome 98+) — JSON fallback is lossy (no Date/Map/Set/undefined)
// but adequate for the JSON-shaped data Apollo + shapeshifter clone in practice.
if (
  typeof (window as unknown as { structuredClone?: unknown }).structuredClone !==
  'function'
) {
  safeDefine(window, 'structuredClone', (value: unknown) =>
    JSON.parse(JSON.stringify(value))
  );
}

// date-fns v4's constructFrom does `Symbol.for('constructDateFrom') in date`
// to detect custom-Date extensions (TZDate, UTCDate). On LG webOS 3 and
// Tizen 2.4–3 (Chrome 38) the core-js Symbol.for shim is partially broken:
// the `in` check is truthy on plain Date objects, but `date[sym]` is
// undefined, so date-fns calls undefined and throws "'undefined' is not a
// function (evaluating 'le[Ke.a](De)')". Mount a working method on
// Date.prototype[sym] so the call site is safe. Modern browsers are
// unaffected: the symbol was not previously present on Date.prototype, so
// they took the `new date.constructor(value)` branch; now they take the
// symbol branch but the method we provide returns `new Date(value)`, which
// is identical for plain Date.
try {
  const sym = Symbol.for('constructDateFrom');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (typeof (Date.prototype as any)[sym] !== 'function') {
    Object.defineProperty(Date.prototype, sym, {
      value: function (value: number | string | Date) {
        return new Date(value as never);
      },
      writable: true,
      configurable: true,
      enumerable: false
    });
  }
} catch {
  // Symbol.for unavailable — date-fns won't reach the symbol branch either
}

enableES5ForImmer();

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
if (typeof globalThis.__DEV__ !== 'boolean') {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  globalThis.__DEV__ = false;
}
